import { FC, JSX, useEffect, useMemo, useState } from "react";
import { ColumnConfig, WafflesThemes, wafflesKeyColumnName, WafflesPaginationProps } from "@app/shared/waffles/exports";
import { Waffles } from "@app/shared/waffles/Waffles";
import PageSection from "@app/shared/PageSection";
import ProviderNetworkTypeBadge from "@app/shared/ProviderNetworkTypeBadge";
import ProviderActionStatusBadge from "@app/shared/ProviderActionStatusBadge";
import { sortProviderNetworkTypes } from "@app/shared/enums/providerNetworkTypeEnum";
import { ProviderBadgeStyle } from "@app/projects/enumerators";
import WaffleRowTooltip from "@app/shared/waffles/WaffleRowTooltip";
import { QESButton } from "@app/shared/qes-standards/Button";
import { ProviderHubContractEntityProviders, ProviderHubContractEntityCcnGroup } from "@app/projects/network-assessment/provider-hub/interfaces";
import { ProviderScoreColumnHeader } from "@app/projects/network-assessment/provider-hub/ProviderScoreColumnHeader";
import { NO_ACTIONS_APPLIED_LABEL, formatCountiesImpacted, formatGapImpactCount, formatScore, formatSpecialtyWithCode } from "@app/projects/network-assessment/provider-hub/utility";

// Synthetic parent key for facilities that have no CCN. AC 193447: "Where there are facilities with no CCN, group
// them underneath section 'Other Facilities'." The backend returns these as a CCN-group with a null Ccn.
const OTHER_FACILITIES_KEY = "other-facilities";
const OTHER_FACILITIES_LABEL = "No Identified CCN";

// Parent row key for a CCN group. Kept in sync with the profile's onExpandCcn parser (ccn: prefix or OTHER key).
const parentKeyForGroup = (group: ProviderHubContractEntityCcnGroup): string =>
  group.ccn ? `ccn:${group.ccn}` : OTHER_FACILITIES_KEY;

interface FacilityTableRow {
  [wafflesKeyColumnName]: string;
  identifier: JSX.Element | string;
  ccn: JSX.Element | string;
  name: JSX.Element | string;
  providerSource: JSX.Element | string;
  status: JSX.Element | string;
  affiliationStrength: string | number;
  gapImpact: string | number;
  specialtiesImpacted: JSX.Element | string;
  activeGapImpact: string | number;
  pendingGapImpact: string | number;
  approvedGapImpact: string | number;
  opportunityGapImpact: string | number;
  countiesImpacted: JSX.Element | string;
  accessPoints: string | number;
  effectiveness: string | number;
  efficiency: string | number;
  motiveAppropriateness: string | number;
  careJourneyCost: string | number;
  careJourneyQuality: string | number;
}

interface ContractEntityFacilitiesTableProps {
  // "ccn" (default): parent-paginated CCN groups (expandable, children lazy-loaded). "npi2": flat list with a CCN column.
  viewBy: "ccn" | "npi2";
  showPci: boolean;
  showMotive: boolean;
  showCareJourney: boolean;
  // Navigates to the Hospital profile for the given CCN (same tab). Wired by the CE profile.
  onCcnClick: (ccn: string) => void;
  // Navigates to the Organization profile for a facility row's own type-2 NPI (same tab).
  onOrganizationClick: (organizationNpi: string) => void;

  // --- NPI (Type 2) flat view ---
  // Flat list of type-2 facility (NPI2) records for the contract entity.
  facilities: ProviderHubContractEntityProviders[];

  // Result-level Gap Impact breakdown capability for the whole result set of the active view, from the backend.
  // Never derived from the rows: the CCN sums are COALESCEd to 0, the flat facility list is capped, and the
  // lazily-loaded CCN children are not on hand here.
  useGapsByRegionStatus: boolean;

  // --- CCN view (Option A parent-pagination) ---
  // Server-paginated CCN (hospital) parent rows.
  ccnGroups: ProviderHubContractEntityCcnGroup[];
  // Children (facilities) keyed by parent row key; filled lazily as groups are expanded.
  childrenByCcn: Map<string, ProviderHubContractEntityProviders[]>;
  // Fetches the children for a parent row key on first expand (Waffles dataExpansionOnToggleClick).
  onExpandCcn: (parentKey: string) => Promise<void>;
  // Pagination for the CCN parent rows.
  ccnPaginationProps: WafflesPaginationProps;
  // While true, Waffles shows a loading spinner instead of the empty-table message.
  loading?: boolean;
}

const networkTypeBadges = (networkTypes: ProviderHubContractEntityProviders["providerNetworkTypes"]): JSX.Element => (
  <>
    {sortProviderNetworkTypes(networkTypes ?? []).map((networkType) => (
      <ProviderNetworkTypeBadge badgeStyle={ProviderBadgeStyle.Outline} key={networkType} providerNetworkType={networkType} />
    ))}
  </>
);

const actionStatusBadges = (statuses: ProviderHubContractEntityProviders["providerActionStatuses"]): JSX.Element => (
  (statuses ?? []).length > 0
    ? <>{statuses.map((status, index) => <ProviderActionStatusBadge badgeStyle={ProviderBadgeStyle.Dot} key={index} status={status} />)}</>
    : <>{NO_ACTIONS_APPLIED_LABEL}</>
);

const providerSourceCell = (facility: ProviderHubContractEntityProviders): JSX.Element => networkTypeBadges(facility.providerNetworkTypes ?? []);

const statusCell = (facility: ProviderHubContractEntityProviders): JSX.Element => actionStatusBadges(facility.providerActionStatuses ?? []);

const ContractEntityFacilitiesTable: FC<ContractEntityFacilitiesTableProps> = (props) => {
  const useRegionStatusBreakdown = props.useGapsByRegionStatus;

  /* oxlint-disable sort-keys */
  const columnConfig: ColumnConfig = {
    identifier: { displayText: "Identifier", sizeContentToFit: true, sticky: true, testId: "ce-facilities-table-identifier", noSort: true },
    // Frozen columns are the two leftmost visible ones: Identifier + CCN in the NPI (Type 2) view, and Identifier +
    // Name in the CCN view (where the CCN column is hidden).
    ccn: { displayText: "CCN", sizeContentToFit: true, sticky: props.viewBy === "npi2", testId: "ce-facilities-table-ccn", hidden: props.viewBy !== "npi2", noSort: true },
    name: { displayText: "Name", sizeContentToFit: true, sticky: props.viewBy === "ccn", testId: "ce-facilities-table-name", noSort: true },
    providerSource: { displayText: "Provider Source", sizeContentToFit: true, testId: "ce-facilities-table-provider-source", tooltip: "Indicates the original database from which the provider data was obtained", noSort: true },
    status: { displayText: "Status", sizeContentToFit: true, testId: "ce-facilities-table-status", tooltip: "Indicates any actions that have been applied to either a specific access point or across access points for a provider", noSort: true },
    affiliationStrength: { displayText: "Affiliation Strength", sizeContentToFit: true, testId: "ce-facilities-table-affiliation-strength", tooltip: "Indicates the strength of the affiliation", noSort: true },
    gapImpact: { displayText: "Gap Impact", sizeContentToFit: true, testId: "ce-facilities-table-gap-impact", tooltip: "Indicates the distinct number of gaps that the provider closes", noSort: true, hidden: useRegionStatusBreakdown },
    specialtiesImpacted: { displayText: "Specialty Groups Impacted", sizeContentToFit: true, testId: "ce-facilities-table-specialty", noSort: true },
    activeGapImpact: { displayText: "Active Gap Impact", sizeContentToFit: true, testId: "ce-facilities-table-active-gap-impact", tooltip: "Count of gaps closeable in Active service area counties", noSort: true, hidden: !useRegionStatusBreakdown },
    pendingGapImpact: { displayText: "Pending Gap Impact", sizeContentToFit: true, testId: "ce-facilities-table-pending-gap-impact", tooltip: "Count of gaps closeable in Pending service area counties", noSort: true, hidden: !useRegionStatusBreakdown },
    approvedGapImpact: { displayText: "Approved Gap Impact", sizeContentToFit: true, testId: "ce-facilities-table-approved-gap-impact", tooltip: "Count of gaps closeable in Approved service area counties", noSort: true, hidden: !useRegionStatusBreakdown },
    opportunityGapImpact: { displayText: "Opportunity Gap Impact", sizeContentToFit: true, testId: "ce-facilities-table-opportunity-gap-impact", tooltip: "Count of gaps closeable in Opportunity-to-expand service area counties", noSort: true, hidden: !useRegionStatusBreakdown },
    countiesImpacted: { displayText: "Counties Impacted", sizeContentToFit: true, testId: "ce-facilities-table-county", noSort: true },
    accessPoints: { displayText: "Access Points", sizeContentToFit: true, testId: "ce-facilities-table-access-points", noSort: true },
    effectiveness: { displayText: "Effectiveness (Quality)", sizeContentToFit: true, testId: "ce-facilities-table-effectiveness", hidden: !props.showPci, noSort: true },
    efficiency: { displayText: "Efficiency (Cost)", sizeContentToFit: true, testId: "ce-facilities-table-efficiency", hidden: !props.showPci, noSort: true },
    motiveAppropriateness: { displayElement: <ProviderScoreColumnHeader variant="motive" />, sizeContentToFit: true, testId: "ce-facilities-table-motive", hidden: !props.showMotive, noSort: true },
    careJourneyCost: { displayElement: <ProviderScoreColumnHeader variant="careJourneyCost" />, sizeContentToFit: true, testId: "ce-facilities-table-carejourney-cost", hidden: !props.showCareJourney, noSort: true },
    careJourneyQuality: { displayElement: <ProviderScoreColumnHeader variant="careJourneyQuality" />, sizeContentToFit: true, testId: "ce-facilities-table-carejourney-quality", hidden: !props.showCareJourney, noSort: true },
  };
  /* oxlint-enable sort-keys */

  // A facility row is the CE's own type-2 organization, so its name links to the Organization profile — the CCN cell
  // and the CCN parent row are what link to the Hospital profile. Plain text when there is no NPI to navigate by.
  const organizationNameCell = (facility: ProviderHubContractEntityProviders): JSX.Element | string => {
    if (!facility.name) {
      return "---";
    }
    if (!facility.npi) {
      return facility.name;
    }
    return (
      <QESButton qesType="link" qesTestId={`ce-facilities__name-link--${facility.npi}`} className="demibold" onClick={() => props.onOrganizationClick(facility.npi)}>
        {facility.name}
      </QESButton>
    );
  };

  /* oxlint-disable sort-keys */
  const childRow = (facility: ProviderHubContractEntityProviders, parentKey: string): FacilityTableRow => ({
    [wafflesKeyColumnName]: `${parentKey}:${facility.npi}`,
    identifier: `NPI (Type 2): ${facility.npi}`,
    ccn: "---",
    name: organizationNameCell(facility),
    providerSource: providerSourceCell(facility),
    status: statusCell(facility),
    affiliationStrength: facility.affiliationStrength != null ? `${Math.round(facility.affiliationStrength * 100)}%` : "---",
    gapImpact: formatGapImpactCount(facility.gapsClosed),
    specialtiesImpacted: <WaffleRowTooltip containerClassName="provider-hub-waffle-tooltip" elements={(facility.specialtiesImpacted ?? []).map(formatSpecialtyWithCode)} numberOfElementsPerRow={3} />,
    activeGapImpact: formatGapImpactCount(facility.activeGapsClosed),
    pendingGapImpact: formatGapImpactCount(facility.pendingGapsClosed),
    approvedGapImpact: formatGapImpactCount(facility.approvedGapsClosed),
    opportunityGapImpact: formatGapImpactCount(facility.opportunityGapsClosed),
    countiesImpacted: <WaffleRowTooltip containerClassName="provider-hub-waffle-tooltip" elements={formatCountiesImpacted(facility.countiesImpacted)} numberOfElementsPerRow={3} />,
    accessPoints: facility.accessPointCount ?? "---",
    effectiveness: formatScore(facility.effectivenessRank),
    efficiency: formatScore(facility.efficiencyRank),
    motiveAppropriateness: formatScore(facility.motiveAppropriateness),
    careJourneyCost: formatScore(facility.careJourneyCost),
    careJourneyQuality: formatScore(facility.careJourneyQuality),
  });

  // A CCN group (parent) row: hospital name + a caret; aggregate cells come from the server-side rollup across
  // the CCN's facilities (distinct provider source / status / specialties / counties, summed gap impact + access points).
  const ccnGroupParentRow = (key: string, identifier: JSX.Element | string, name: JSX.Element | string, group: ProviderHubContractEntityCcnGroup): FacilityTableRow => ({
    [wafflesKeyColumnName]: key,
    identifier,
    ccn: "---",
    name,
    providerSource: networkTypeBadges(group.providerNetworkTypes ?? []),
    status: actionStatusBadges(group.providerActionStatuses ?? []),
    affiliationStrength: group.affiliationStrength != null ? `${Math.round(group.affiliationStrength * 100)}%` : "---",
    gapImpact: formatGapImpactCount(group.gapsClosed),
    specialtiesImpacted: <WaffleRowTooltip containerClassName="provider-hub-waffle-tooltip" elements={(group.specialtiesImpacted ?? []).map(formatSpecialtyWithCode)} numberOfElementsPerRow={3} />,
    activeGapImpact: formatGapImpactCount(group.activeGapsClosed),
    pendingGapImpact: formatGapImpactCount(group.pendingGapsClosed),
    approvedGapImpact: formatGapImpactCount(group.approvedGapsClosed),
    opportunityGapImpact: formatGapImpactCount(group.opportunityGapsClosed),
    countiesImpacted: <WaffleRowTooltip containerClassName="provider-hub-waffle-tooltip" elements={formatCountiesImpacted(group.countiesImpacted ?? [])} numberOfElementsPerRow={3} />,
    accessPoints: group.accessPointCount,
    effectiveness: formatScore(group.effectivenessRank),
    efficiency: formatScore(group.efficiencyRank),
    motiveAppropriateness: formatScore(group.motiveAppropriateness),
    careJourneyCost: formatScore(group.careJourneyCost),
    careJourneyQuality: formatScore(group.careJourneyQuality),
  });
  /* oxlint-enable sort-keys */

  // Build parent rows straight from the server-paginated CCN groups, and seed an expansion entry per parent so the
  // expand toggle renders. Entries stay empty until onExpandCcn lazily fills childrenByCcn.
  const { parents, expansions } = useMemo(() => {
    const parentRows: FacilityTableRow[] = [];
    const expansionMap = new Map<string, FacilityTableRow[]>();

    for (const group of props.ccnGroups) {
      const key = parentKeyForGroup(group);
      const identifier = group.ccn
        ? (
          <QESButton qesType="link" qesTestId={`ce-facilities__ccn-link--${group.ccn}`} className="demibold" onClick={() => props.onCcnClick(group.ccn!)}>
            {`CCN: ${group.ccn}`}
          </QESButton>
        )
        : OTHER_FACILITIES_LABEL;
      // The CCN parent groups multiple facilities, so its name is plain text — the `CCN: …` identifier is the hospital
      // link, and each child row links its own name to its Organization profile.
      const name = group.ccn ? (group.hospitalName ?? group.ccn) : "---";
      parentRows.push(ccnGroupParentRow(key, identifier, name, group));
      expansionMap.set(key, (props.childrenByCcn.get(key) ?? []).map((f) => childRow(f, key)));
    }

    return { expansions: expansionMap, parents: parentRows };
  }, [props.ccnGroups, props.childrenByCcn, props.onCcnClick, props.onOrganizationClick]);

  // View by NPI (Type 2): flat rows, one per facility, with a CCN column linking to the Hospital profile.
  const flatRows = useMemo<FacilityTableRow[]>(() =>
    props.facilities.map((facility) => ({
      ...childRow(facility, "flat"),
      ccn: facility.ccn
        ? <QESButton qesType="link" qesTestId={`ce-facilities__ccn-link--${facility.ccn}`} className="demibold" onClick={() => props.onCcnClick(facility.ccn!)}>{facility.ccn}</QESButton>
        : "---",
    })),
    [props.facilities, props.onCcnClick, props.onOrganizationClick]
  );

  // Client-side pagination for the flat NPI (Type 2) view (the parent fetches the facility list in one large page),
  // so it gets the same pagination footer + scroll behaviour as the other tabs.
  const [flatPageIndex, setFlatPageIndex] = useState<number>(1);
  const [flatPageSize, setFlatPageSize] = useState<number>(50);

  useEffect(() => {
    setFlatPageIndex(1);
  }, [flatRows.length]);

  const pagedFlatRows = useMemo(() => flatRows.slice((flatPageIndex - 1) * flatPageSize, flatPageIndex * flatPageSize), [flatRows, flatPageIndex, flatPageSize]);

  const flatPaginationProps: WafflesPaginationProps = {
    changePage: (pageNumber: number) => setFlatPageIndex(pageNumber),
    enableBackendPagination: false,
    handlePageSizeChange: (newPageSize: number) => { setFlatPageSize(newPageSize); setFlatPageIndex(1); },
    pageSizeOptions: [50, 100, 150],
    recordsPerPage: flatPageSize,
    resetToFirstPage: false,
    showPageSizeSelector: true,
    skip: (flatPageIndex - 1) * flatPageSize,
    totalRecords: flatRows.length,
  };

  return (
    <PageSection>
      {props.viewBy === "npi2" ? (
        <Waffles<FacilityTableRow>
          columns={columnConfig}
          data={pagedFlatRows}
          enablePagination={true}
          totalNumberOfRecords={flatRows.length}
          paginationProps={flatPaginationProps}
          loading={props.loading}
          emptyTableMessage="No facilities found for this contract entity."
          fullWidth={true}
          testId="provider-hub__ce-facilities-table"
          theme={WafflesThemes.ContractEntityDetails}
          disableStickyHeader={true}
          variant="frozen-border"
        />
      ) : (
        <Waffles<FacilityTableRow>
          columns={columnConfig}
          data={parents}
          dataExpansions={expansions}
          dataExpansionOnToggleClick={(expansionKey: string) => props.onExpandCcn(expansionKey)}
          enablePagination={true}
          totalNumberOfRecords={props.ccnPaginationProps.totalRecords}
          paginationProps={props.ccnPaginationProps}
          loading={props.loading}
          emptyTableMessage="No facilities found for this contract entity."
          fullWidth={true}
          testId="provider-hub__ce-facilities-table"
          theme={WafflesThemes.ContractEntityDetails}
          disableStickyHeader={true}
          variant="frozen-border"
        />
      )}
    </PageSection>
  );
};

export default ContractEntityFacilitiesTable;
